import React, { useCallback, useEffect, useState, useRef } from 'react';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import styled from 'styled-components';
import { Input } from 'reactstrap';
import { useTranslation } from '@hooks';
import cloneDeep from 'lodash/cloneDeep';
const TreeSelect = styled(DropdownTreeSelect)`
    ${props =>
        ['simpleSelect', 'radioSelect'].find(v => v === props.mode) &&
        `
        .tag-list {
            vertical-align: middle;
        }
        .tag-list .tag-item {
            line-height: 1;
            width: 80%;
        }
        .tag-list .tag-item:not(:last-child) {
            display: none;
        }
        .tag-list .tag-item:last-child>span {
            width: 150px;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            display: inline-block;
        }
        `}
    ${props =>
        props.isMap &&
        `
        .dropdown-content {
           background-color: #fff !important;   
        }  
        .tag-item > input {
           background-color:  #fff !important;
        }    
        .dropdown-trigger {
           background-color: #fff !important;
        }
    `}
`;

const findParentNode = (floorList, upperFloorId, parents = []) => {
    const node = floorList.find(v => v.floorId === upperFloorId);
    if (node && node.upperFloorId) {
        parents.push(node.upperFloorId);
        findParentNode(floorList, node.upperFloorId, parents);
    }
    return parents;
};

const findParentNodeSubRows = (path, searchList) => {
    if (path.length > 1) {
        const currPath = path.shift();
        return findParentNodeSubRows(path, searchList.find(node => node.floorId === currPath).children);
    } else {
        return searchList;
    }
};

const makeHierarchyFloorInfo = floorInfo => {
    const tempFloorInfo = cloneDeep(floorInfo);
    tempFloorInfo.sort((a, b) => a.levelNo - b.levelNo);
    return tempFloorInfo.reduce((acc, floor) => {
        const expandableFloor = {
            authorized: 0,
            unAuthorized: 0,
            ...floor,
            label: floor.floorName,
            value: floor.floorId,
            // checked: true,
            children: [],
        };
        if (floor.levelNo === 0) {
            acc.push(expandableFloor);
        } else {
            const path = floor.floorIdPath.split('>');
            const subRows = findParentNodeSubRows(path, acc);
            subRows.push(expandableFloor);
        }
        return acc;
    }, []);
};

const getExpandFloorBySelected = (floorList, selectedFloorList) => {
    let expandFloorBySelected = [];
    selectedFloorList.forEach(v => {
        const parents = findParentNode(floorList, v);
        expandFloorBySelected = expandFloorBySelected.concat(parents);
    });
    return expandFloorBySelected;
};

const makeSelectedName = (floorList, selectedFloorList) => {
    return selectedFloorList
        .reduce((acc, curr) => {
            const floor = floorList.find(floorInfo => floorInfo.floorId === curr);
            if (floor) {
                acc.push(floor.floorName);
            }
            return acc;
        }, [])
        .join(',');
};

/**
 * FloorTreeSelect 컴포넌트
 * @param floorList,
 * @param selectedFloorStr
 * @param innerRef
 * @param formName
 * @param mode
 * @param handleChange
 * @param showDropdown
 * @param nullable 렌더링 시 리스트의 첫번째 값 set 여부 대한 파라미터
 *
 * nullable 추가 설명
 * 기존에 렌더링 시 리스트의 첫번째 값을 set
 * 이러한 첫번째 값 set 여부를 묻는 nullable 파라미터 추가
 *
 * @author jinbeom-jung
 *
 * */

const FloorTreeSelect = ({
    floorList,
    selectedFloorStr = '',
    innerRef,
    formName = 'floor',
    mode = 'multiSelect',
    handleChange,
    showDropdown,
    nullable = false,
    isMap = false,
    disabled = false,
}) => {
    const t = useTranslation('TreeSelect');
    const treeDropdownRef = useRef();
    const origin = floorList;
    const [selectedFloor, setSelectedFloor] = useState(selectedFloorStr);
    const [selectedLabel, setSelectedLabel] = useState(makeSelectedName(origin, selectedFloor.split(',')));
    const [expandedFloor, setExpandedFloor] = useState(
        getExpandFloorBySelected(origin, selectedFloor.split(',')).reduce((acc, curr) => {
            acc[curr] = true;
            return acc;
        }, {}),
    );

    const [floorTreeList, setFloorTreeList] = useState(makeHierarchyFloorInfo(origin));
    const onChange = useCallback(
        ({ value, checked, _children, floorId, floorName, expanded }, selectedNodes) => {
            // if (value === 'selectAll') {
            //     setFloorTreeList(prev => {
            //         for (let i = 0; i < prev.length; i++) {
            //             prev[i].checked = checked;
            //         }
            //         console.log(prev);
            //         return prev;
            //     });
            // }
            // console.log({ value, checked, _children, floorId, floorName, expanded });
            // if (['simpleSelect', 'radioSelect'].find(v => v === mode)) {
            //     let selectedFloorId = '';
            //     if (_children.length === 0) {
            //         selectedFloorId = floorId;
            //         setSingleSelectLabel(floorName);
            //     } else {
            //         setExpandedFloor(prev => {
            //             prev[floorId] = !expanded;
            //             return prev;
            //         });
            //     }
            //     setSelectedFloor(selectedFloorId);
            // } else {
            //     setSelectedFloor(selectedNodes.map(v => v.floorId).join(','));
            // }
            setSelectedFloor(selectedNodes.map(v => v.floorId).join(','));
            if (typeof handleChange === 'function') {
                handleChange(selectedNodes);
            }
        },
        [handleChange],
    );
    const onNodeToggle = useCallback(
        currentNode => {
            // console.log('onNodeToggle::', currentNode);
            setExpandedFloor(prev => {
                prev[currentNode.floorId] = currentNode.expanded;
                return prev;
            });
            const isSingleSelect = ['simpleSelect', 'radioSelect'].find(v => v === mode);
            const selectedFloorList = selectedFloor.split(',');
            setFloorTreeList(
                makeHierarchyFloorInfo(
                    origin.map(v => {
                        const copyV = { ...v };
                        if (selectedFloorList.includes(v.floorId)) {
                            copyV.checked = true;
                        }
                        copyV.expanded = expandedFloor[copyV.floorId];
                        const hasChildren = origin.find(floor => floor.upperFloorId === copyV.floorId);
                        copyV.disabled = !!(isSingleSelect && hasChildren);
                        return copyV;
                    }),
                ),
            );
        },
        [mode, origin],
    );

    const handleMouseWheelEvent = function (e) {
        e.stopPropagation();
    };
    useEffect(() => {
        if (nullable) {
            if (!selectedFloorStr && floorList.length > 0) {
                setSelectedFloor(floorList[0].floorId);
                handleChange([floorList[0]]);
            }
        }

        if (treeDropdownRef.current) {
            treeDropdownRef.current.node.addEventListener('wheel', handleMouseWheelEvent);
        }
        return () => {
            if (treeDropdownRef.current) {
                treeDropdownRef.current.node.removeEventListener('wheel', handleMouseWheelEvent);
            }
        };
    }, []);

    useEffect(() => {
        setSelectedFloor(selectedFloorStr);
    }, [selectedFloorStr]);

    useEffect(() => {
        const isSingleSelect = ['simpleSelect', 'radioSelect'].find(v => v === mode);
        const selectedFloorList = selectedFloor.split(',');

        setFloorTreeList(
            makeHierarchyFloorInfo(
                origin.map(v => {
                    const copyV = { ...v };
                    if (selectedFloorList.includes(v.floorId)) {
                        copyV.checked = true;
                    }
                    copyV.expanded = expandedFloor[copyV.floorId];
                    const hasChildren = origin.find(floor => floor.upperFloorId === copyV.floorId);
                    copyV.disabled = !!(isSingleSelect && hasChildren);
                    return copyV;
                }),
            ),
        );
        setSelectedLabel(makeSelectedName(origin, selectedFloorList));
    }, [origin, selectedFloor, expandedFloor]);

    return (
        <div className={'pnt-select--group d-flex w-100'}>
            {innerRef && <Input type="hidden" name={formName} innerRef={innerRef} defaultValue={selectedFloor} />}
            <TreeSelect
                ref={treeDropdownRef}
                data={floorTreeList}
                onChange={onChange}
                onNodeToggle={onNodeToggle}
                showPartiallySelected={true}
                keepTreeOnSearch
                keepChildrenOnSearch
                showDropdown={showDropdown}
                mode={mode}
                className="react-select-item"
                isMap={isMap}
                disabled={disabled}
                // readOnly={true}
                texts={{
                    placeholder: selectedLabel || t('Select floor'),
                    noMatches: t('No matches found'),
                }}
            />
        </div>
    );
};

export default React.memo(FloorTreeSelect);
